import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import ScheduleConfigurationFields, { isScheduleConfigurationValid, SCHEDULE_TIMEZONE } from './ScheduleConfigurationFields';
import ScanScheduleModal from './ScanScheduleModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/scanner/schedules`;
const SCHEDULE_OPEN_KEY = 'scanner_schedule_open';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const defaults = () => ({
  name: '', frequency_minutes: 15, weekdays: [0, 1, 2, 3, 4],
  timezone: SCHEDULE_TIMEZONE,
  window_start: '09:30', window_end: '16:00', enabled: false,
});
const errorMessage = error => {
  const detail = error.response?.data?.detail;
  return Array.isArray(detail) ? detail.map(item => item.msg).join('; ') : detail || error.message;
};

const scheduleProgress = schedule => {
  const summary = schedule?.last_summary || {};
  const total = Number(summary.total ?? schedule?.symbols?.length ?? 0);
  const scanned = Number(summary.scanned ?? 0);
  const errorCount = Array.isArray(summary.errors) ? summary.errors.length : 0;
  const processedRaw = Number(summary.processed ?? (scanned + errorCount));
  const processed = total > 0 ? Math.max(0, Math.min(processedRaw, total)) : Math.max(0, processedRaw);
  return { processed, total };
};

export default function AutomaticScanner({ formData, onScanComplete, onScanNotification, onRunScanner, scanning = false, scanProgress = { done: 0, total: 0 } }) {
  const { t } = useTranslation();
  const [configuration, setConfiguration] = useState(defaults);
  const [scheduleExpanded, setScheduleExpanded] = useState(() => {
    try {
      return localStorage.getItem(SCHEDULE_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const schedulePanelId = useId();
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const previousRuns = useRef({});
  const refreshResults = useRef(onScanComplete);
  refreshResults.current = onScanComplete;
  const notifyResults = useRef(onScanNotification);
  notifyResults.current = onScanNotification;

  const toggleSchedule = () => {
    const nextExpanded = !scheduleExpanded;
    setScheduleExpanded(nextExpanded);
    try {
      localStorage.setItem(SCHEDULE_OPEN_KEY, String(nextExpanded));
    } catch (failure) {
      console.error('Failed to save scanner schedule preference:', failure);
    }
  };

  const loadSchedules = useCallback(async () => {
    try {
      const { data } = await axios.get(API, { headers: headers() });
      data.forEach(schedule => {
        if (['running', 'cancelling'].includes(schedule.last_status) || !schedule.last_run_at || previousRuns.current[schedule.id] === schedule.last_run_at) return;
        const summary = schedule.last_summary || {};
        const findings = summary.findings || [];
        if (findings.some(result => result.resistance_levels?.length || result.support_levels?.length || result.signals?.length)) {
          notifyResults.current?.({
            source: 'automatic', name: schedule.name, scanned: summary.scanned || 0,
            findings, errors: summary.errors || [], deferred: summary.deferred || 0,
            signals: findings.flatMap(result => (result.signals || []).map(signal => ({
              symbol: result.symbol, signal_type: signal.signal_type,
              entry_price: signal.entry, stop_price: signal.stop, target_price: signal.target,
              breakout_confirmation_time: signal.breakout_confirmation_time, breakout_confirmation_candle: signal.breakout_confirmation_candle,
              retest_time: signal.retest_time, retest_candle: signal.retest_candle,
              confirmation_time: signal.confirmation_time, confirmation_candle: signal.confirmation_candle,
            }))),
          });
        }
      });
      if (data.some(schedule => schedule.last_run_at && previousRuns.current[schedule.id] && previousRuns.current[schedule.id] !== schedule.last_run_at)) {
        refreshResults.current?.();
      }
      previousRuns.current = Object.fromEntries(data.map(schedule => [schedule.id, schedule.last_run_at || 'never']));
      setSchedules(data);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedules();
    const timer = setInterval(loadSchedules, 15000);
    return () => clearInterval(timer);
  }, [loadSchedules]);

  const perform = async operation => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await operation();
      await loadSchedules();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (busy) return;
    let validationMessage = '';
    if (!formData.symbols.length) {
      validationMessage = t('schedule_symbols_required', { defaultValue: 'Select at least one symbol above before creating a schedule.' });
    } else if (!configuration.name.trim()) {
      validationMessage = t('schedule_name_required', { defaultValue: 'Enter a schedule name.' });
    } else if (!Number.isInteger(configuration.frequency_minutes) || configuration.frequency_minutes < 1 || configuration.frequency_minutes > 10080) {
      validationMessage = t('schedule_frequency_invalid', { defaultValue: 'Run every must be a whole number between 1 and 10080 minutes.' });
    } else if (!configuration.weekdays.length) {
      validationMessage = t('schedule_days_required', { defaultValue: 'Select at least one day.' });
    } else if (!configuration.window_start || !configuration.window_end || configuration.window_start > configuration.window_end) {
      validationMessage = t('schedule_times_invalid', { defaultValue: 'Enter valid From and Until times, with From no later than Until (NY).' });
    } else if (!isScheduleConfigurationValid(configuration)) {
      validationMessage = t('schedule_configuration_invalid', { defaultValue: 'Check the schedule settings before creating a schedule.' });
    }
    if (validationMessage) {
      setError(validationMessage);
      setMessage('');
      return;
    }
    return perform(async () => {
      const { symbols, ...settings } = formData;
      const payload = { ...configuration, symbols, settings };
      await axios.post(API, payload, { headers: headers() });
      setConfiguration(defaults());
      setMessage(t('schedule_saved', { defaultValue: 'Schedule saved.' }));
    });
  };

  const updateSchedule = async (scheduleId, payload) => {
    await axios.put(`${API}/${scheduleId}`, payload, { headers: headers() });
    setMessage(t('schedule_saved', { defaultValue: 'Schedule saved.' }));
    await loadSchedules();
  };

  const deleteSchedule = async scheduleId => {
    await axios.delete(`${API}/${scheduleId}`, { headers: headers() });
    setMessage(t('schedule_deleted', { defaultValue: 'Schedule deleted.' }));
    await loadSchedules();
  };

  const cancelSchedule = async scheduleId => {
    await axios.post(`${API}/${scheduleId}/cancel`, {}, { headers: headers() });
    setMessage(t('schedule_cancel_requested', { defaultValue: 'Cancellation requested. Active symbol scans may finish; future runs are paused.' }));
    await loadSchedules();
  };

  const runSchedule = async scheduleId => {
    const { data } = await axios.post(`${API}/${scheduleId}/run`, {}, { headers: headers() });
    setSchedules(current => current.map(schedule => schedule.id === scheduleId ? data : schedule));
    setMessage(t('schedule_run_started', { defaultValue: 'Scan started using the saved schedule settings.' }));
  };


  return (
    <>
      {onRunScanner && <button
        type="button"
        onClick={onRunScanner}
        disabled={scanning || !formData.symbols.length}
        className="w-full bg-green-600 text-white px-2 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition font-semibold text-xs shadow-lg"
      >
        {scanning ? (
          <span className="flex items-center justify-center gap-2" role="status">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 0 18-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A 7.962 7.962 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {scanProgress.total > 1
              ? `${t('running_scan')} ${scanProgress.done}/${scanProgress.total}`
              : t('running_scan')}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2M16 4h2a2 2 0 012 2v2M20 16v2a2 2 0 01-2 2h-2M8 20H6a2 2 0 01-2-2v-2M4 12h16" />
            </svg>
            {t('run_scanner')}
          </span>
        )}
      </button>}
      {onRunScanner && <hr className="border-gray-200" />}
      <section className="space-y-4 rounded-xl bg-blue-50 p-4" aria-label={t('automatic_scanner', { defaultValue: 'Automatic scanner' })}>
      <h3>
        <button
          type="button"
          aria-expanded={scheduleExpanded}
          aria-controls={schedulePanelId}
          onClick={toggleSchedule}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg bg-blue-50 px-3 py-2 text-left text-sm font-semibold text-blue-900 transition-colors hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        >
          <span>{t('auto_scanner', { defaultValue: 'Auto Scanner' })}</span>
          <span className="text-gray-500" aria-hidden="true">
            <svg className={`h-4 w-4 transition-transform motion-reduce:transition-none ${scheduleExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>
      </h3>
      <div id={schedulePanelId} hidden={!scheduleExpanded} className="space-y-4">
        <ScheduleConfigurationFields configuration={configuration} onChange={setConfiguration} disabled={busy} showEnabled={false} />
        <button type="button" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-white px-2 py-2 text-xs font-semibold disabled:bg-gray-400" disabled={busy || !configuration.name.trim() || !configuration.weekdays.length} onClick={save}>
          <Save className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
          {t('schedule_create', { defaultValue: 'Save Auto Scanner' })}
        </button>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {message && <p role="status" className="text-sm text-green-700">{message}</p>}
        <h3 className="font-semibold">{t('saved_auto_scanners', { defaultValue: 'Saved Auto Scanners' })}</h3>
        {loading ? <p>{t('loading')}</p> : !schedules.length && <p className="text-sm text-gray-500">{t('schedule_empty', { defaultValue: 'No schedules yet.' })}</p>}
        {schedules.length > 0 && <div className="overflow-x-auto rounded-lg border">
          <table className="w-full table-auto text-left text-sm">
            <caption className="sr-only">{t('saved_auto_scanners', { defaultValue: 'Saved Auto Scanners' })}</caption>
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th scope="col" className="whitespace-nowrap px-2 py-3">{t('schedule_name_header', { defaultValue: 'Name' })}</th>
                <th scope="col" className="whitespace-nowrap px-2 py-3">{t('schedule_time_frame', { defaultValue: 'Time Frame' })}</th>
                <th scope="col" className="whitespace-nowrap px-2 py-3">{t('schedule_enabled', { defaultValue: 'Enabled' })}</th>
                <th scope="col" className="whitespace-nowrap px-2 py-3">{t('schedule_status', { defaultValue: 'Status' })}</th>
                <th scope="col"><span className="sr-only">{t('schedule_actions', { defaultValue: 'Actions' })}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {schedules.map(schedule => (
                <tr key={schedule.id} className="hover:bg-gray-50">
                  <th scope="row" className="whitespace-nowrap px-2 py-3 font-medium"><span title={schedule.name}>{schedule.name}</span></th>
                  <td className="whitespace-nowrap px-2 py-3">{schedule.settings.period}/{schedule.settings.interval}</td>
                  <td className="whitespace-nowrap px-2 py-3">
                    <span className={`text-xs font-medium ${schedule.enabled ? 'text-green-700' : 'text-red-700'}`}>
                      {schedule.enabled
                        ? t('enabled', { defaultValue: 'Enabled' })
                        : t('disabled', { defaultValue: 'Disabled' })}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-3 align-middle">
                    {['running', 'cancelling'].includes(schedule.last_status)
                      ? (() => {
                          const progress = scheduleProgress(schedule);
                          return progress.total > 0
                            ? <span className="text-xs font-medium text-blue-700">({progress.processed}/{progress.total})</span>
                            : <span className="text-xs text-gray-600">{schedule.last_status}</span>;
                        })()
                      : <span className="text-xs text-gray-600">{schedule.last_status || '-'}</span>}
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex items-center justify-end">
                      <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded text-gray-600 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-50" disabled={busy} aria-label={t('schedule_open_details', { defaultValue: 'Open schedule details' }) + ': ' + schedule.name} aria-haspopup="dialog" onClick={() => setSelectedSchedule(schedule)}>
                        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>
      {selectedSchedule && <ScanScheduleModal key={selectedSchedule.id} schedule={schedules.find(schedule => schedule.id === selectedSchedule.id) || selectedSchedule} onClose={() => setSelectedSchedule(null)} onSave={updateSchedule} onDelete={deleteSchedule} onCancel={cancelSchedule} onRun={runSchedule} />}
      </section>
    </>
  );
}













